import { useState, useEffect } from "react";
import { useArticles } from "@/hooks/useArticles";
import { CreateArticleForm } from "@/components/articles/forms/CreateArticleForm";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Upload, X, Image } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Article } from "@/types/article";

interface OrganizationMember {
  id: string;
  full_name: string;
  organization: {
    id: string;
    name: string;
  };
}

const ArticlesManagement = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [memberData, setMemberData] = useState<OrganizationMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [articleToDelete, setArticleToDelete] = useState<Article | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  
  const { 
    articles, 
    loading: articlesLoading, 
    createArticle, 
    updateArticle, 
    deleteArticle 
  } = useArticles();

  useEffect(() => {
    const loadMemberData = async () => {
      try {
        const memberId = sessionStorage.getItem('organization_member_id');
        if (!memberId) {
          throw new Error("No member ID found");
        }

        const { data: memberResult, error: memberError } = await supabase
          .from('organization_members')
          .select(`
            id,
            full_name,
            organization:organization_id (
              id,
              name
            )
          `)
          .eq('id', memberId)
          .single();

        if (memberError) throw memberError;
        if (!memberResult) throw new Error("Member not found");

        setMemberData(memberResult);
      } catch (error) {
        console.error("Error loading member data:", error);
        toast.error("Failed to load member data");
      } finally {
        setLoading(false);
      }
    };

    loadMemberData();
  }, []);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image is too large. Maximum size is 5MB');
      return;
    }

    setEditImageFile(file);
    setRemoveExistingImage(false);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setEditImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeEditImage = () => {
    setEditImageFile(null);
    setEditImagePreview(null);
    setRemoveExistingImage(true);
  };

  const uploadEditImage = async (): Promise<string | null> => {
    if (!editImageFile) return null;

    try {
      const fileExt = editImageFile.name.split('.').pop();
      const filePath = `articles/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, editImageFile);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleUpdateArticle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingArticle) return;

    setIsUpdating(true);
    try {
      let imageUrl = editingArticle.image_url;

      // Handle image removal
      if (removeExistingImage) {
        imageUrl = null;
      }
      // Upload new image if selected
      else if (editImageFile) {
        const uploadedUrl = await uploadEditImage();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        }
      }

      // Update the article with the new image URL
      const { error } = await supabase
        .from('organization_news')
        .update({ 
          title: editingArticle.title, 
          content: editingArticle.content,
          image_url: imageUrl 
        })
        .eq('id', editingArticle.id);

      if (error) throw error;

      toast.success('Article updated successfully');
      
      // Reset editing states
      setEditingArticle(null);
      setEditImageFile(null);
      setEditImagePreview(null);
      setRemoveExistingImage(false);
      
      // Refresh articles list
      window.location.reload();
    } catch (error) {
      console.error('Error updating article:', error);
      toast.error('Failed to update article');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditClick = (article: Article) => {
    setEditingArticle(article);
    setEditImageFile(null);
    setEditImagePreview(null);
    setRemoveExistingImage(false);
  };

  const handleDeleteClick = (article: Article) => {
    setArticleToDelete(article);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!articleToDelete) return;
    
    setIsDeleting(true);
    try {
      const success = await deleteArticle(articleToDelete.id);
      if (success) {
        setIsDeleteDialogOpen(false);
        setArticleToDelete(null);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  if (loading) {
    return (
      <div className="relative flex size-full min-h-screen flex-col bg-white overflow-x-hidden" style={{ fontFamily: 'Newsreader, "Noto Sans", sans-serif' }}>
        <div className="px-40 flex flex-1 justify-center py-5">
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#111418]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-white overflow-x-hidden" style={{ fontFamily: 'Newsreader, "Noto Sans", sans-serif' }}>
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-4 md:px-8 lg:px-40 flex flex-1 justify-center py-5">
          <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
            <div className="flex flex-wrap justify-between gap-3 p-4">
              <p className="text-[#111418] tracking-light text-xl md:text-2xl lg:text-[32px] font-bold leading-tight min-w-72">
                Manage News Articles
              </p>
              <Button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-8 px-4 bg-[#f0f2f4] text-[#111418] text-sm font-medium leading-normal hover:bg-[#dce0e5]"
                variant="ghost"
              >
                <Plus className="h-4 w-4 mr-2" />
                <span className="truncate">{showCreateForm ? 'Cancel' : 'Create New'}</span>
              </Button>
            </div>

            {showCreateForm && (
              <div className="px-4 mb-6">
                <div className="rounded-lg border border-[#dce0e5] bg-white p-6">
                  <h3 className="text-[#111418] text-lg font-bold mb-4">Create New Article</h3>
                  <CreateArticleForm 
                    onCreateArticle={async (title, content, imageUrl) => {
                      const success = await createArticle(title, content, imageUrl);
                      if (success) setShowCreateForm(false);
                      return success;
                    }}
                  />
                </div>
              </div>
            )}

            <div className="px-4 py-3">
              <div className="flex overflow-hidden rounded-lg border border-[#dce0e5] bg-white">
                <table className="flex-1">
                  <thead>
                    <tr className="bg-white">
                      <th className="px-4 py-3 text-left text-[#111418] w-[400px] text-sm font-medium leading-normal">Title</th>
                      <th className="px-4 py-3 text-left text-[#111418] w-[400px] text-sm font-medium leading-normal">Publication Date</th>
                      <th className="px-4 py-3 text-left text-[#111418] w-[400px] text-sm font-medium leading-normal">Author</th>
                      <th className="px-4 py-3 text-left text-[#637588] w-60 text-sm font-medium leading-normal">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {articlesLoading ? (
                      <tr>
                        <td colSpan={4} className="h-[72px] px-4 py-2 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#111418]"></div>
                            <span className="ml-2 text-[#637588]">Loading articles...</span>
                          </div>
                        </td>
                      </tr>
                    ) : articles.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="h-[72px] px-4 py-2 text-center">
                          <p className="text-[#637588]">No articles found</p>
                        </td>
                      </tr>
                    ) : (
                      articles.map((article) => (
                        <tr key={article.id} className="border-t border-t-[#dce0e5]">
                          {editingArticle?.id === article.id ? (
                            <>
                              <td className="px-4 py-4" colSpan={4}>
                                <form onSubmit={handleUpdateArticle} className="space-y-4">
                                  <Input
                                    value={editingArticle.title}
                                    onChange={(e) => setEditingArticle({
                                      ...editingArticle,
                                      title: e.target.value
                                    })}
                                    required
                                    className="text-sm"
                                    placeholder="Article title"
                                  />
                                  <Textarea
                                    value={editingArticle.content}
                                    onChange={(e) => setEditingArticle({
                                      ...editingArticle,
                                      content: e.target.value
                                    })}
                                    required
                                    className="min-h-[100px] text-sm"
                                    placeholder="Article content"
                                  />
                                  
                                  {/* Image upload section */}
                                  <div className="space-y-2">
                                    <label className="text-sm font-medium text-[#111418]">Image (optional)</label>
                                    
                                    {editImagePreview || (editingArticle.image_url && !removeExistingImage) ? (
                                      <div className="relative">
                                        <img 
                                          src={editImagePreview || editingArticle.image_url} 
                                          alt="Preview" 
                                          className="w-full h-32 object-cover rounded-lg border border-[#dce0e5]"
                                        />
                                        <Button
                                          type="button"
                                          variant="destructive"
                                          size="icon"
                                          className="absolute top-2 right-2 h-6 w-6"
                                          onClick={removeEditImage}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="border-2 border-dashed border-[#dce0e5] rounded-lg p-4">
                                        <div className="text-center">
                                          <Image className="mx-auto h-8 w-8 text-[#637588] mb-2" />
                                          <p className="text-xs text-[#637588] mb-2">
                                            {removeExistingImage ? 'Image will be removed. Click to add a new one.' : 'Add an image to your article'}
                                          </p>
                                          <Input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageSelect}
                                            className="hidden"
                                            id="edit-image-upload"
                                          />
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => document.getElementById('edit-image-upload')?.click()}
                                          >
                                            <Upload className="h-3 w-3 mr-1" />
                                            Choose Image
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  <div className="flex gap-2">
                                    <Button 
                                      type="submit" 
                                      size="sm"
                                      disabled={isUpdating}
                                      className="bg-[#111418] hover:bg-[#637588] text-white"
                                    >
                                      {isUpdating ? 'Saving...' : 'Save'}
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setEditingArticle(null);
                                        setEditImageFile(null);
                                        setEditImagePreview(null);
                                        setRemoveExistingImage(false);
                                      }}
                                      disabled={isUpdating}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </form>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="h-[72px] px-4 py-2 w-[400px] text-[#111418] text-sm font-normal leading-normal">
                                {article.title}
                              </td>
                              <td className="h-[72px] px-4 py-2 w-[400px] text-[#637588] text-sm font-normal leading-normal">
                                {formatDate(article.created_at)}
                              </td>
                              <td className="h-[72px] px-4 py-2 w-[400px] text-[#637588] text-sm font-normal leading-normal">
                                {memberData?.full_name || 'Unknown'}
                              </td>
                              <td className="h-[72px] px-4 py-2 w-60">
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditClick(article)}
                                    className="text-[#637588] hover:text-[#111418] p-1"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteClick(article)}
                                    className="text-[#637588] hover:text-red-600 p-1"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDeleteDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete Article"
        description={`Are you sure you want to delete "${articleToDelete?.title}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ArticlesManagement;
